/** @jsx isolateComponent */
import type { ReactNode } from 'react';
import { css } from '@emotion/react';

import { isolateComponent, labelStyles } from '@zapier/style-encapsulation';
import { Dropdown } from '@zapier/design-system';
import type { DropdownProps, FloatingMenuItem } from '@zapier/design-system';

import { ServiceIcon } from '../ServiceIcon';

type Props = DropdownProps & {
  /**
   * Consumer supplied function that returns an icon image URL for a given menu
   * item. This is necessary because services do not curently support SVG
   * icons, only raster icons.
   */
  getIconUrlForItem: (item: FloatingMenuItem) => string;
};

const Styles = labelStyles('ServiceDropdown', {
  serviceIcon: css`
    &[data-disabled] {
      opacity: 0.6;
    }
  `,
});

/**
 * A custom version of `Dropdown` for selecting a service. Renders icons appropriate for each service.
 */
export const ServiceDropdown = ({ getIconUrlForItem, ...props }: Props) => {
  const renderIcon = (item: FloatingMenuItem): ReactNode =>
    !!item && (
      <span
        css={Styles.serviceIcon}
        data-disabled={props.isDisabled ? '' : undefined}
      >
        <ServiceIcon
          ariaHidden={true}
          serviceName={props.getLabelForItem(item)}
          size={30}
          src={getIconUrlForItem(item)}
        />
      </span>
    );
  return <Dropdown {...props} renderIcon={renderIcon} />;
};
